
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { ApplicationError } from "@/lib/errors/types";

interface SignUpFormProps {
  isSubmitting: boolean;
  setIsSubmitting: (value: boolean) => void;
}

export const SignUpForm = ({ isSubmitting, setIsSubmitting }: SignUpFormProps) => {
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [formErrors, setFormErrors] = useState({
    email: "",
    password: "",
    fullName: "",
  });

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) return "L'email est requis";
    if (!emailRegex.test(email)) return "Format d'email invalide";
    return "";
  };

  const validatePassword = (password: string) => {
    if (!password) return "Le mot de passe est requis";
    if (password.length < 6) return "Le mot de passe doit contenir au moins 6 caractères";
    return "";
  };

  const validateFullName = (name: string) => {
    if (!name) return "Le nom complet est requis";
    if (name.trim().length < 2) return "Le nom doit contenir au moins 2 caractères";
    return "";
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setIsSubmitting(true);
      
      // Validation
      const emailError = validateEmail(email);
      const passwordError = validatePassword(password);
      const fullNameError = validateFullName(fullName);
      
      setFormErrors({
        email: emailError,
        password: passwordError,
        fullName: fullNameError,
      });

      if (emailError || passwordError || fullNameError) {
        setIsSubmitting(false);
        return;
      }

      // Nettoyage des données
      const sanitizedEmail = email.trim().toLowerCase();
      const sanitizedFullName = fullName.trim();
      await signUp(sanitizedEmail, password, sanitizedFullName);
      
      // Rediriger vers welcome après inscription réussie
      navigate('/welcome');
    } catch (error) {
      console.error("Erreur lors de l'inscription:", error);
      throw new ApplicationError(
        "Une erreur est survenue lors de l'inscription",
        "error",
        { originalError: error }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSignUp} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="signup-fullname">Nom complet</Label>
        <Input
          id="signup-fullname"
          type="text"
          autoComplete="name"
          value={fullName}
          onChange={(e) => {
            setFullName(e.target.value);
            setFormErrors({ ...formErrors, fullName: "" });
          }}
          className={formErrors.fullName ? "border-red-500" : ""}
          required
          disabled={isSubmitting}
        />
        {formErrors.fullName && (
          <p className="text-red-500 text-sm">{formErrors.fullName}</p>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="signup-email">Email</Label>
        <Input
          id="signup-email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setFormErrors({ ...formErrors, email: "" });
          }}
          className={formErrors.email ? "border-red-500" : ""}
          required
          disabled={isSubmitting}
        />
        {formErrors.email && (
          <p className="text-red-500 text-sm">{formErrors.email}</p>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="signup-password">Mot de passe</Label>
        <Input
          id="signup-password"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            setFormErrors({ ...formErrors, password: "" });
          }}
          className={formErrors.password ? "border-red-500" : ""}
          required
          disabled={isSubmitting}
        />
        {formErrors.password && (
          <p className="text-red-500 text-sm">{formErrors.password}</p>
        )}
      </div>
      <Button type="submit" className="w-full" disabled={isSubmitting}>
        {isSubmitting ? "Inscription en cours..." : "S'inscrire"}
      </Button>
    </form>
  );
};
